// Web MIDI input layer. Acquires MIDI access in the secure context,
// enumerates inputs, subscribes to the chosen device, and maintains the set of
// currently-held pitches that the engine compares against each frame.

import { HeldKeys } from "./held-keys";

const NOTE_ON = 0x90;
const NOTE_OFF = 0x80;
const CONTROL_CHANGE = 0xb0;
const SUSTAIN_CC = 64;

export interface MidiPort {
  id: string;
  name: string;
}

/** A musical MIDI event handed to the performance recorder (note edge or sustain edge). */
type MidiRecordEvent =
  | { kind: "note"; pitch: number; on: boolean; velocity: number; timeMs: number }
  | { kind: "pedal"; down: boolean; timeMs: number };

/**
 * Compact display name for a Web MIDI port. ALSA/Chromium reports verbose,
 * repetitive names like "Williams WP01:Williams WP01 MIDI 1" — keep the client
 * name (before the first ':') and cap the length so the UI doesn't wrap.
 */
export function shortMidiName(name: string): string {
  const base = ((name.split(":")[0] ?? "").trim() || name.trim()).replace(/\s+/g, " ");
  return base.length > 20 ? `${base.slice(0, 19)}…` : base;
}

/**
 * ALSA's "Midi Through" loopback (and similar virtual ports) carry no notes, so
 * they must never be auto-selected as the input device.
 */
export function isLoopbackPort(name: string): boolean {
  return /through/i.test(name);
}

/**
 * Choose which input port to auto-select: prefer a previously-saved real device,
 * else the first real device, else none. Never returns a loopback port — if the
 * only ports present are loopbacks, returns null so the app waits for a real one.
 */
export function pickAutoDevice(ports: MidiPort[], savedId: string | null): string | null {
  const real = ports.filter((p) => !isLoopbackPort(p.name));
  if (savedId && real.some((p) => p.id === savedId)) return savedId;
  return real[0]?.id ?? null;
}

export class MidiInput {
  private readonly keys = new HeldKeys();
  /** Pitches currently held (incl. sustained via the pedal), for the engine. */
  get held(): ReadonlySet<number> {
    return this.keys.matching;
  }
  /** Onset velocity (0..1) per currently-held pitch — for the scorer's dynamics. */
  get heldVelocity(): ReadonlyMap<number, number> {
    const m = new Map<number, number>();
    for (const pitch of this.keys.matching) m.set(pitch, this.keys.velocityOf(pitch));
    return m;
  }
  /** Called whenever the held set changes (for immediate visual feedback). */
  onChange: (() => void) | null = null;
  /** Performance-recorder hook — fires for note/sustain events the command layer did NOT
   *  consume (so live-strip / escape keys never get recorded). The recorder ignores it
   *  unless armed. Timestamps are the raw `MIDIMessageEvent.timeStamp` (real wall-clock). */
  onRecord: ((e: MidiRecordEvent) => void) | null = null;

  /**
   * Optional command-layer hook. Gets each note event (pitch, on, timestamp)
   * before the held set; returning true consumes it (suppressed from play).
   */
  private noteFilter: ((pitch: number, on: boolean, timeMs: number) => boolean) | null = null;

  setNoteFilter(fn: (pitch: number, on: boolean, timeMs: number) => boolean): void {
    this.noteFilter = fn;
  }

  private access: MIDIAccess | null = null;
  private current: MIDIInput | null = null;

  /** Request MIDI access. Throws if denied or unsupported. */
  async init(): Promise<void> {
    if (!navigator.requestMIDIAccess) {
      throw new Error("Web MIDI is unavailable — use Chromium over http://localhost.");
    }
    this.access = await navigator.requestMIDIAccess({ sysex: false });
  }

  /** The shared `MIDIAccess`, so `MidiOutput` reuses it (no second request). */
  midiAccess(): MIDIAccess | null {
    return this.access;
  }

  /** Name of the currently-selected input — `MidiOutput` auto-pairs its output by it. */
  currentName(): string | null {
    return this.current?.name ?? null;
  }

  /** Currently-connected input ports. */
  ports(): MidiPort[] {
    if (!this.access) return [];
    return [...this.access.inputs.values()].map((p) => ({
      id: p.id,
      name: p.name ?? p.id,
    }));
  }

  /** Subscribe to a device by id. Detaches any previous device first. */
  select(id: string): void {
    if (!this.access) return;
    if (this.current) this.current.onmidimessage = null;
    this.keys.clear();

    const port = this.access.inputs.get(id) ?? null;
    this.current = port;
    if (port) port.onmidimessage = this.handleMessage;
    this.onChange?.();
  }

  /** Register a callback for device connect/disconnect events. */
  onStateChange(cb: () => void): void {
    if (this.access) this.access.onstatechange = () => cb();
  }

  private handleMessage = (event: MIDIMessageEvent): void => {
    const data = event.data;
    if (!data || data.length < 3) return;
    const command = (data[0] ?? 0) & 0xf0;
    const a = data[1] ?? 0;
    const b = data[2] ?? 0;
    const ts = event.timeStamp;

    let changed = false;
    if (command === NOTE_ON && b > 0) {
      if (this.noteFilter?.(a, true, ts)) return;
      this.onRecord?.({ kind: "note", pitch: a, on: true, velocity: b / 127, timeMs: ts });
      changed = this.keys.noteOn(a, b / 127); // 0..1 onset velocity (was dropped)
    } else if (command === NOTE_OFF || (command === NOTE_ON && b === 0)) {
      if (this.noteFilter?.(a, false, ts)) return;
      this.onRecord?.({ kind: "note", pitch: a, on: false, velocity: 0, timeMs: ts });
      changed = this.keys.noteOff(a);
    } else if (command === CONTROL_CHANGE && a === SUSTAIN_CC) {
      this.onRecord?.({ kind: "pedal", down: b >= 64, timeMs: ts });
      changed = this.keys.setPedal(b >= 64);
    } else return;

    if (changed) this.onChange?.();
  };
}
